import { useState, useEffect } from 'react';
import { CashApplicationSettings as SettingsType } from '@dill/dill-shared';
import { Gauge, Robot, SortAscending, FloppyDisk, Info, Prohibit, Plus, X } from 'phosphor-react';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { DillButton, DillToggle } from '../../../general/Dill';
import {
  CashAppLockedBanner,
  lockedCardClass,
  useCashAppEnabled,
} from '../components/CashAppLocked';
import {
  fetchBankTransactionsThunk,
  updateCashAppSettingsThunk,
} from '../../../../redux/services/cashApplicationService';

export const CashApplicationSettings = () => {
  const dispatch = useAppDispatch();
  const savedSettings = useAppSelector((state) => state.cashApplication.settings);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;

  const [pendingSettings, setPendingSettings] = useState<SettingsType>(savedSettings);
  const [isDirty, setIsDirty] = useState(false);
  const [newIgnoredPayer, setNewIgnoredPayer] = useState('');

  // Read-only while the feature is off: these settings only describe what ingestion does, and
  // saving them would imply the pipeline is about to honour them.
  const cashAppEnabled = useCashAppEnabled();
  const locked = !cashAppEnabled;

  // Settings arrive with the supplier document, which may resolve after first render.
  useEffect(() => {
    if (!isDirty) {
      setPendingSettings(savedSettings);
    }
  }, [savedSettings, isDirty]);

  const updateSetting = <K extends keyof SettingsType>(key: K, value: SettingsType[K]) => {
    setPendingSettings((s) => ({ ...s, [key]: value }));
    setIsDirty(true);
  };

  const ignoredPayers = pendingSettings.ignoredDepositPayers ?? [];

  // Staged, so Save is the only thing that writes.
  const addIgnoredPayer = () => {
    const trimmed = newIgnoredPayer.trim();
    if (!trimmed) return;
    const alreadyListed = ignoredPayers.some(
      (existing) => existing.toLowerCase() === trimmed.toLowerCase()
    );
    if (alreadyListed) {
      toast.info('That description is already being ignored.', { autoClose: 2500 });
      setNewIgnoredPayer('');
      return;
    }
    updateSetting('ignoredDepositPayers', [...ignoredPayers, trimmed]);
    setNewIgnoredPayer('');
  };

  const removeIgnoredPayer = (pattern: string) => {
    updateSetting(
      'ignoredDepositPayers',
      ignoredPayers.filter((entry) => entry !== pattern)
    );
  };

  const handleSave = async () => {
    if (!supplierId) return;
    // Only the flags and the ignore list are persisted; the confidence thresholds are not yet
    // honoured by the matcher, so sending them would imply an effect they do not have.
    const result = await dispatch(
      updateCashAppSettingsThunk({
        supplierId,
        applyToOldestWhenNoRemittance: pendingSettings.applyToOldestWhenNoRemittance,
        autoPostEnabled: pendingSettings.autoPostEnabled,
        lockboxEnabled: pendingSettings.lockboxEnabled,
        ignoredDepositPayers: ignoredPayers,
      })
    );
    if (result.meta.requestStatus === 'fulfilled') {
      setIsDirty(false);
      const dismissed =
        (result.payload as { ignoredDismissed?: number } | undefined)?.ignoredDismissed ?? 0;
      // Report the sweep — its effect otherwise happens on a screen they are not looking at.
      toast.success(
        dismissed > 0
          ? `Settings saved. ${dismissed} deposit(s) removed from the unmatched queue.`
          : 'Settings saved successfully.',
        { autoClose: 3000 }
      );
      // The store still holds the swept deposits as unmatched.
      if (dismissed > 0) {
        dispatch(fetchBankTransactionsThunk({ supplierId }));
      }
    } else {
      toast.error((result.payload as string) ?? 'Failed to save settings.', { autoClose: 4000 });
    }
  };

  const settings = pendingSettings;

  return (
    <div className="flex flex-col h-full px-6 py-4 gap-6 max-w-3xl overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Cash Application Settings</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Configure confidence thresholds, auto-posting rules, and fallback behavior.
          </p>
        </div>
        <DillButton
          onClick={handleSave}
          label="Save Changes"
          variant="primary"
          icon={<FloppyDisk size={14} weight="bold" />}
          isDisabled={locked || !isDirty}
          height={36}
        />
      </div>

      {locked && <CashAppLockedBanner />}

      {/* Confidence Thresholds */}
      <div
        aria-disabled={locked}
        className={
          'bg-white rounded-xl border border-gray-200 shadow-sm p-5' + lockedCardClass(locked)
        }>
        <div className="flex items-center gap-2 mb-4">
          <Gauge size={20} weight="duotone" className="text-blue-600" />
          <h3 className="text-sm font-semibold text-gray-900">Confidence Thresholds</h3>
        </div>

        <div className="space-y-6">
          {/* Auto-Post Threshold */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Auto-Post Minimum Confidence
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Payments matched at or above this threshold are posted automatically without
                  review.
                </p>
              </div>
              <span className="text-lg font-semibold text-gray-900 tabular-nums w-16 text-right">
                {Math.round(settings.autoPostMinConfidence * 100)}%
              </span>
            </div>
            <input
              type="range"
              disabled={locked}
              min="50"
              max="100"
              step="1"
              value={Math.round(settings.autoPostMinConfidence * 100)}
              onChange={(e) =>
                updateSetting('autoPostMinConfidence', parseInt(e.target.value) / 100)
              }
              className="w-full h-2 rounded-full appearance-none bg-gray-200 accent-emerald-600"
            />
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* 1-Click Review Threshold */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  1-Click Review Minimum Confidence
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  Payments between this threshold and the auto-post threshold appear in the 1-click
                  review queue.
                </p>
              </div>
              <span className="text-lg font-semibold text-gray-900 tabular-nums w-16 text-right">
                {Math.round(settings.oneClickReviewMinConfidence * 100)}%
              </span>
            </div>
            <input
              type="range"
              disabled={locked}
              min="30"
              max="90"
              step="1"
              value={Math.round(settings.oneClickReviewMinConfidence * 100)}
              onChange={(e) =>
                updateSetting('oneClickReviewMinConfidence', parseInt(e.target.value) / 100)
              }
              className="w-full h-2 rounded-full appearance-none bg-gray-200 accent-blue-600"
            />
            <div className="flex justify-between mt-1 text-xs text-gray-400">
              <span>30%</span>
              <span>90%</span>
            </div>
          </div>

          {/* Visual Indicator */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center gap-1.5 mb-2">
              <Info size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Routing Preview</span>
            </div>
            <div className="flex h-4 rounded-full overflow-hidden">
              <div
                className="bg-red-400 transition-all"
                style={{
                  width: `${Math.round(settings.oneClickReviewMinConfidence * 100)}%`,
                }}
                title="Exceptions Queue"
              />
              <div
                className="bg-amber-400 transition-all"
                style={{
                  width: `${Math.round(
                    (settings.autoPostMinConfidence - settings.oneClickReviewMinConfidence) * 100
                  )}%`,
                }}
                title="1-Click Review"
              />
              <div
                className="bg-emerald-400 transition-all"
                style={{
                  width: `${Math.round((1 - settings.autoPostMinConfidence) * 100)}%`,
                }}
                title="Auto-Post"
              />
            </div>
            <div className="flex justify-between mt-1.5 text-xs">
              <span className="text-red-600 font-medium">
                Exceptions (&lt;{Math.round(settings.oneClickReviewMinConfidence * 100)}%)
              </span>
              <span className="text-amber-600 font-medium">
                1-Click Review ({Math.round(settings.oneClickReviewMinConfidence * 100)}-
                {Math.round(settings.autoPostMinConfidence * 100)}%)
              </span>
              <span className="text-emerald-600 font-medium">
                Auto-Post (&ge;{Math.round(settings.autoPostMinConfidence * 100)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Auto-Post Toggle */}
      <div
        aria-disabled={locked}
        className={
          'bg-white rounded-xl border border-gray-200 shadow-sm p-5' + lockedCardClass(locked)
        }>
        <div className="flex items-center gap-2 mb-4">
          <Robot size={20} weight="duotone" className="text-purple-600" />
          <h3 className="text-sm font-semibold text-gray-900">Auto-Post</h3>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Enable Auto-Posting</p>
            <p className="text-xs text-gray-500 mt-0.5">
              When enabled, high-confidence matches are posted to your ERP automatically. Disable to
              review all payments manually.
            </p>
          </div>
          <DillToggle
            value={settings.autoPostEnabled}
            onChange={(v) => updateSetting('autoPostEnabled', v)}
            isDisabled={locked}
          />
        </div>
      </div>

      {/* Fallback Rules */}
      <div
        aria-disabled={locked}
        className={
          'bg-white rounded-xl border border-gray-200 shadow-sm p-5' + lockedCardClass(locked)
        }>
        <div className="flex items-center gap-2 mb-4">
          <SortAscending size={20} weight="duotone" className="text-amber-600" />
          <h3 className="text-sm font-semibold text-gray-900">Fallback Rules</h3>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">
              Apply to Oldest Invoices When No Remittance
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              When a payment arrives with no remittance details and no confident match can be found,
              apply the payment to the oldest open invoices for that customer.
            </p>
          </div>
          <DillToggle
            value={settings.applyToOldestWhenNoRemittance}
            onChange={(v) => updateSetting('applyToOldestWhenNoRemittance', v)}
            isDisabled={locked}
          />
        </div>
      </div>

      {/* Ignored Deposits */}
      <div
        aria-disabled={locked}
        className={
          'bg-white rounded-xl border border-gray-200 shadow-sm p-5' + lockedCardClass(locked)
        }>
        <div className="flex items-center gap-2 mb-1">
          <Prohibit size={20} weight="duotone" className="text-rose-600" />
          <h3 className="text-sm font-semibold text-gray-900">Ignored Deposits</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Deposits whose bank description contains any of these are not customer payments, and are
          kept out of the unmatched deposits queue. Matching ignores case and punctuation, and a
          part of the description is enough — you do not need the trace numbers the bank appends.
        </p>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newIgnoredPayer}
            disabled={locked}
            placeholder="e.g. PREAUTHORIZED ACH CREDIT INTUIT"
            onChange={(e) => setNewIgnoredPayer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addIgnoredPayer();
              }
            }}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:bg-gray-50"
          />
          <DillButton
            onClick={addIgnoredPayer}
            label="Add"
            variant="secondary"
            icon={<Plus size={14} weight="bold" />}
            isDisabled={locked || !newIgnoredPayer.trim()}
            height={38}
          />
        </div>

        {ignoredPayers.length === 0 ? (
          <p className="text-sm text-gray-400 italic">
            Nothing is being ignored. Every deposit in your feed reaches the unmatched deposits
            queue.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {ignoredPayers.map((pattern) => (
              <span
                key={pattern}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-600/20">
                {pattern}
                <button
                  type="button"
                  disabled={locked}
                  aria-label={`Stop ignoring ${pattern}`}
                  onClick={() => removeIgnoredPayer(pattern)}
                  className="p-0.5 rounded-full hover:bg-rose-100 disabled:opacity-40">
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
